import * as React from 'react';
import * as Sentry from '@sentry/react';
import { formatAppVersion } from '@helpers/formatAppVersion';
import { getEnvironmentFromHostname } from '@helpers/getEnvironmentFromHostname';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundaryComponent } from 'app/pages/ErrorBoundary';
import { queryClient } from 'app/query/queryClient';
import { createRoot } from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { App } from './app/App';
import { initializeBrowserFaro } from './app/telemetry/initializeBrowserFaro';

import './index.css';

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js', { scope: '/' }).catch((error) => {
    console.error('Service Worker registration failed:', error);
  });
}

const environment = getEnvironmentFromHostname(window.location.hostname);
const version = formatAppVersion(import.meta.env.VITE_APP_VERSION, import.meta.env.VITE_COMMIT_SHA);
const commitSha = import.meta.env.VITE_COMMIT_SHA;
const browserFaro = initializeBrowserFaro({
  endpoint: import.meta.env.VITE_FARO_ENDPOINT,
  sampleRate: import.meta.env.VITE_FARO_SESSION_SAMPLING_RATE,
  version,
  environment,
});

console.info(`AutoScript ${version} - ${environment}`);

const isDevEnvironment = window.location.hostname === 'localhost' || window.location.hostname === 'mac.local';
if (!isDevEnvironment) {
  Sentry.init({
    dsn: 'https://3ede02bc0ebf4b93b07b00f1b99502b8@o1135867.ingest.sentry.io/6204281',
    tracesSampleRate: 1.0,
    ...(commitSha ? { release: commitSha } : {}),
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        traceFetch: !browserFaro,
        traceXHR: !browserFaro,
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}
const root = createRoot(container);

root.render(
  <Sentry.ErrorBoundary
    fallback={({ error, componentStack }) => <ErrorBoundaryComponent error={error} componentStack={componentStack} />}
  >
    <QueryClientProvider client={queryClient}>
      <App />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </Sentry.ErrorBoundary>,
);
